import api from '@/apis'
import { v4 as uuidv4 } from 'uuid'
import Vuetify from '@/plugins/vuetify'
import fileDownload from 'js-file-download'

export default {
  state: {
    table: {
      search: '',
      loading: false,
      page: 1,
      items_per_page: 10,
      total_items: 0,
      headers: [
        { title: Vuetify.locale.t('$vuetify.__DatatableHeader.file_name_or_source'), key: 'filename' },
        { title: Vuetify.locale.t('$vuetify.__DatatableHeader.type'), key: 'doc_type', width: 140, sortable: false },
        { title: Vuetify.locale.t('$vuetify.__DatatableHeader.status'), key: 'status', width: 140 },
        { title: Vuetify.locale.t('$vuetify.__DatatableHeader.enable'), key: 'enabled', width: 110, sortable: false },
        { title: Vuetify.locale.t('$vuetify.__DatatableHeader.date_uploaded'), key: 'create_datetime', width: 160 },
        { title: '', key: 'action', sortable: false, width: 110 }
      ],
      items: [],
      selected: []
    },
    capabilities: { source_enable: false },
    conversation: {
      session_id: null,
      loading: false,
      query_in_flight: false,
      active_request_id: null,
      conversations: []
    }
  },
  mutations: {
    // table
    SET_MRC_AI_SCAN_TABLE_KEYWORD(state, payload) {
      state.table.keyword = payload
    },
    SET_MRC_AI_SCAN_TABLE_LOADING(state, payload) {
      state.table.loading = payload
    },
    SET_MRC_AI_SCAN_TABLE_ITEMS(state, payload) {
      state.table.items = payload
    },
    SET_MRC_AI_SCAN_TABLE_SELECTED(state, payload) {
      state.table.selected = payload
    },
    SET_MRC_AI_SCAN_TABLE_TOTAL_ITEMS(state, payload) {
      state.table.total_items = payload
    },
    SET_MRC_AI_SCAN_TABLE_SEARCH(state, payload) {
      state.table.search = payload
    },
    SET_MRC_AI_SCAN_TABLE_PAGE(state, payload) {
      state.table.page = payload
    },
    SET_MRC_AI_SCAN_TABLE_ITEMS_PER_PAGE(state, payload) {
      state.table.items_per_page = payload
    },
    UPDATE_MRC_AI_SCAN_TABLE_ITEM(state, { id, patch }) {
      const idx = state.table.items.findIndex((x) => x.id === id)
      if (idx >= 0) state.table.items[idx] = { ...state.table.items[idx], ...patch }
    },
    SET_MRC_AI_SCAN_CAPABILITIES(state, payload) {
      state.capabilities = { ...state.capabilities, ...payload }
    },

    // conversation
    SET_MRC_AI_SCAN_SESSION_ID(state, payload) {
      state.conversation.session_id = payload
    },
    SET_MRC_AI_SCAN_CONVERSATION_LOADING(state, payload) {
      state.conversation.loading = payload
    },
    SET_MRC_AI_SCAN_QUERY_IN_FLIGHT(state, payload) {
      state.conversation.query_in_flight = payload
    },
    SET_MRC_AI_SCAN_ACTIVE_REQUEST_ID(state, payload) {
      state.conversation.active_request_id = payload
    },
    RESET_MRC_AI_SCAN_CONVERSATION(state) {
      state.conversation.conversations = []
      state.conversation.active_request_id = null
    },
    APPEND_MRC_AI_SCAN_CONVERSATION(state, payload) {
      state.conversation.conversations.push(payload)
    }
  },
  actions: {
    async fetchMrcAiScanList({ state, commit }, payload) {
      try {
        commit('SET_MRC_AI_SCAN_SESSION_ID', uuidv4())
        commit('SET_MRC_AI_SCAN_TABLE_LOADING', true)
        commit('SET_MRC_AI_SCAN_TABLE_ITEMS', [])
        // Drop any batch selection: ids are page/language-scoped, so a refresh,
        // tab switch, search, or pagination must not carry stale selections.
        commit('SET_MRC_AI_SCAN_TABLE_SELECTED', [])
        commit('RESET_MRC_AI_SCAN_CONVERSATION', [])

        if ('search' in payload) commit('SET_MRC_AI_SCAN_TABLE_SEARCH', payload.search)

        const page = payload?.options?.page ? payload.options.page : 1
        commit('SET_MRC_AI_SCAN_TABLE_PAGE', page)
        const itemsPerPage = payload?.options?.itemsPerPage ? payload.options.itemsPerPage : state.table.items_per_page
        commit('SET_MRC_AI_SCAN_TABLE_ITEMS_PER_PAGE', itemsPerPage)
        const sortBy = payload?.options?.sortBy[0] ? payload.options.sortBy[0] : null
        const SORT_KEY_MAP = { filename: 'file_name', create_datetime: 'date_uploaded' }
        let sort_by = 'date_uploaded'
        let order = 'desc'
        if (sortBy) {
          sort_by = SORT_KEY_MAP[sortBy.key] ?? sortBy.key
          order = sortBy.order
        }

        const response = await api.getMrcDocList({
          language: payload.language,
          search: state.table.search,
          limit: itemsPerPage,
          offset: page * itemsPerPage - itemsPerPage,
          sort_by: sort_by,
          order: order
        })
        if (response.data.success) {
          const table_items = JSON.parse(JSON.stringify(response.data.data)).filter((item) => item.status !== 'Removed')
          commit('SET_MRC_AI_SCAN_TABLE_ITEMS', table_items)
          commit('SET_MRC_AI_SCAN_TABLE_TOTAL_ITEMS', response.data.total)

          commit('SET_MRC_AI_SCAN_CONVERSATION_LOADING', false)
          if (table_items.length <= 0) commit('SET_MRC_AI_SCAN_CONVERSATION_LOADING', true)
          table_items.forEach((item) => {
            commit('SET_MRC_AI_SCAN_TABLE_TOTAL_ITEMS', response.data.total)
            if (item.status === 'Scanning') {
              commit('SET_MRC_AI_SCAN_CONVERSATION_LOADING', true)
            }
          })
        } else throw Error(response.data.message)
      } catch (error) {
        commit('SET_SNACKBAR', { status: true, color: 'error', text: error.message, icon: 'close', timeout: '2000' })
      } finally {
        commit('SET_MRC_AI_SCAN_TABLE_LOADING', false)
      }
    },
    async exportMrcAiScanList({ rootState, commit }, { languages }) {
      try {
        commit('SET_SNACKBAR', { status: true, color: 'loading', text: Vuetify.locale.t('$vuetify.__SnackBar.downloading'), loading: true })

        const t = (key, ...args) => Vuetify.locale.t(key, ...args)
        const device = rootState.auth.device_list.find((d) => d.id === rootState.auth.selected_device)
        const device_name = device ? device.description : ''

        // The backend now builds the workbook (single query per language -> no
        // OFFSET paging, so no duplicate/missing rows; no client-side SheetJS).
        // Locale stays here: send the already-translated header/value strings so
        // the backend only fills them in. Fixed header order keeps columns stable.
        const language_labels = (languages || []).reduce((acc, l) => {
          acc[l] = t(`$vuetify.__Lang.${String(l).toLowerCase()}`)
          return acc
        }, {})
        const labels = {
          headers: [
            t('$vuetify.__AI.mrc.export.device_name'),
            t('$vuetify.__General.language'),
            t('$vuetify.__DatatableHeader.file_name_or_source'),
            t('$vuetify.__DatatableHeader.type'),
            t('$vuetify.__DatatableHeader.status'),
            t('$vuetify.__DatatableHeader.enable'),
            t('$vuetify.__DatatableHeader.date_uploaded')
          ],
          language: language_labels,
          type: { web: t('$vuetify.__AI.mrc.type_website'), file: t('$vuetify.__AI.mrc.type_document') },
          status_crawled: t('$vuetify.__AI.mrc.status_crawled'),
          enabled: t('$vuetify.__AI.mrc.export.enabled'),
          disabled: t('$vuetify.__AI.mrc.export.disabled')
        }

        const response = await api.exportMrcDocList({
          params: { device_id: rootState.auth.selected_device, languages: languages || [], device_name, labels }
        })

        const now = new Date()
        const pad = (n) => String(n).padStart(2, '0')
        const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
        fileDownload(response.data, `mrc_ai_scan_documents_${stamp}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')

        commit('SET_SNACKBAR', { status: true, color: 'success', text: Vuetify.locale.t('$vuetify.__SnackBar.download'), icon: 'check', timeout: '2000' })
      } catch (error) {
        // The endpoint responds with a blob, so an error body (the backend's
        // JSON {message}) arrives as a Blob and error.message is only axios's
        // generic status text — read the blob to surface the real reason.
        let text = error.message
        try {
          const data = error?.response?.data
          if (data instanceof Blob) text = JSON.parse(await data.text()).message || text
        } catch (e) {
          /* fall back to the generic message */
        }
        commit('SET_SNACKBAR', { status: true, color: 'error', text, icon: 'close', timeout: '2000' })
      }
    },
    async toggleMrcDocEnable({ commit }, { item, enabled }) {
      commit('UPDATE_MRC_AI_SCAN_TABLE_ITEM', { id: item.id, patch: { enabled } })
      try {
        const r = await api.toggleMrcDocEnable({ params: { id: item.id, enabled } })
        if (!r.data.success) throw Error(r.data.message)
        commit('RESET_MRC_AI_SCAN_CONVERSATION')
        commit('SET_MRC_AI_SCAN_SESSION_ID', uuidv4())
      } catch (e) {
        commit('UPDATE_MRC_AI_SCAN_TABLE_ITEM', { id: item.id, patch: { enabled: !enabled } })
        commit('SET_SNACKBAR', { status: true, color: 'error', text: e.message, icon: 'close', timeout: '2000' })
      }
    },
    async fetchMrcCapabilities({ commit }) {
      try {
        const r = await api.getMrcHealthcheck()
        const features = r?.data?.data?.features || r?.data?.features || {}
        commit('SET_MRC_AI_SCAN_CAPABILITIES', { source_enable: features.source_enable === true })
      } catch (_) {
        commit('SET_MRC_AI_SCAN_CAPABILITIES', { source_enable: false })
      }
    },
    async queryMrcAiScanConversation({ state, commit }, { project_language, query_params, project_params }) {
      const QUERY_TIMEOUT_MS = 30000
      const request_id = uuidv4()
      let cancelled = false

      commit('SET_MRC_AI_SCAN_ACTIVE_REQUEST_ID', request_id)
      commit('SET_MRC_AI_SCAN_QUERY_IN_FLIGHT', true)

      let timeoutHandle
      const timeoutPromise = new Promise((_, reject) => {
        timeoutHandle = setTimeout(() => {
          const err = new Error('MRC query timed out')
          err.isMrcTimeout = true
          reject(err)
        }, QUERY_TIMEOUT_MS)
      })

      // Discard late completion if the timeout/race already settled, or another action
      // (RESET_MRC_AI_SCAN_CONVERSATION / a newer dispatch) invalidated this request.
      const isStale = () => cancelled || state.conversation.active_request_id !== request_id

      const queryPromise = (async () => {
        const get_project_response = await api.readMrcProject()
        if (!get_project_response.data.success) throw Error(get_project_response.data.message)

        const projects = get_project_response.data.data
        const project_names = Object.keys(projects)
        const project_languages = project_names.map((name) => name.split('-')[2])
        if (!project_languages.includes(project_language)) {
          const create_project_response = await api.createMrcProject(project_params)
          if (!create_project_response.data.success) throw Error(create_project_response.data.message)
        }

        const query_response = await api.queryMrc(query_params)
        if (!query_response.data.success) throw Error(query_response.data.message)

        if (isStale()) return
        const result = query_response.data
        commit('APPEND_MRC_AI_SCAN_CONVERSATION', { text: result.speech, file_uploaded: result.Name, type: 'answer' })
      })()
      // Promise.race only awaits the winner; suppress unhandled-rejection from the loser.
      queryPromise.catch(() => {})

      try {
        await Promise.race([queryPromise, timeoutPromise])
      } catch (error) {
        cancelled = true
        const text = error && error.isMrcTimeout ? Vuetify.locale.t('$vuetify.__AI.mrc.query_timeout') : (error && error.message) || 'Error'
        commit('SET_SNACKBAR', { status: true, color: 'error', text, icon: 'close', timeout: '2000' })
        console.log(error && error.message)
      } finally {
        clearTimeout(timeoutHandle)
        commit('SET_MRC_AI_SCAN_QUERY_IN_FLIGHT', false)
      }
    },
    async previewMrcDocument(_ctx, { id }) {
      const r = await api.previewMrcDocument({ id })
      return r.data
    }
  }
}
